import { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'

import type { PropsWithChildren, ReactElement } from 'react'

import RedirectRouter from './components/RedirectRouter'
import SuspenseFallback from './components/SuspenseFallback'
import { AUTHORIZATIONS } from './const'
import { ClustersContextProvider } from './providers/Clusters'
import { getDeprecatedService, isDeprecatedServiceRemoved } from './utils/deprecation'
import { isProduction } from './utils/env'
import NotFound from './views/404'
import AppsUsers from './views/Accounts'
import AppsUsersDetails from './views/Accounts/Details'
import CompareCohortsRouter from './views/Analytics/CohortedAnalytics/CompareCohorts/CompareCohortsRouter'
import GraphConfigsList from './views/Analytics/CohortedAnalytics/GraphConfigsList'
import AnalyticsRouter from './views/Analytics/Router'
import AppConfig from './views/AppConfig'
import AppsManagement from './views/AppsManagement'
import AppDetail from './views/AppsManagement/AppDetail'
import AppsSetup from './views/AppsSetup'
import ASOAdvanced from './views/AppStoreOptimization/Advanced'
import ASOOverview from './views/AppStoreOptimization/Overview'
import Builds from './views/Builds'
import Categories from './views/Catalog/Categories'
import CreateTemplate from './views/Catalog/CreateTemplate'
import Daily from './views/Catalog/Daily'
import Explore from './views/Catalog/Explore'
import Checklists from './views/Checklists'
import ChecklistDetails from './views/Checklists/Details'
import ClusterAppInfos from './views/Competitors/Clusters/ClusterAppInfos'
import ClusterDetails from './views/Competitors/Clusters/ClusterDetails'
import Clusters from './views/Competitors/Clusters/ClustersList'
import Feature from './views/Competitors/Clusters/Features'
import ClustersLayout from './views/Competitors/Clusters/Layout'
import CompetitorsCompanies from './views/Competitors/Companies'
import CompetitorsRecommendations from './views/Competitors/Recommendations'
import Arbitration from './views/DynamicScreens/Arbitration'
import AssetDetail from './views/DynamicScreens/AssetsManagement/AssetDetails'
import AssetsListing from './views/DynamicScreens/AssetsManagement/AssetsListing'
import NavigationGraphsDetails from './views/DynamicScreens/NavigationGraphs/Details'
import NavigationGraphListing from './views/DynamicScreens/NavigationGraphs/Listing'
import PageContainerDetails from './views/DynamicScreens/PageContainers/Details'
import PageContainersListing from './views/DynamicScreens/PageContainers/Listing'
import Placements from './views/DynamicScreens/Placements'
import Templates from './views/DynamicScreens/Templates'
import Events from './views/Events'
import ExternalLinks from './views/ExternalLinks'
import FileFullScreen from './views/FileFullScreen'
import Files from './views/Files'
import Glossary from './views/Glossary'
import Home from './views/Home/index'
import AuthorizationDetails from './views/IAM/authorizations/AuthorizationDetails'
import AuthorizationListing from './views/IAM/authorizations/AuthorizationListing'
import GroupDetails from './views/IAM/groups/GroupDetails'
import GroupListing from './views/IAM/groups/GroupListing'
import RoleDetails from './views/IAM/roles/RoleDetails'
import RoleListing from './views/IAM/roles/RoleListing'
import AccountDetails from './views/IAM/users/UserDetails'
import Accounts from './views/IAM/users/UserListing'
import InAppPurchasesRouter from './views/InAppPurchases/Router'
import ProfilePosts from './views/Profiles/Posts'
import ProfilePostComments from './views/Profiles/Posts/Comments'
import ProfileProjects from './views/Profiles/Projects'
import Releases from './views/Releases'
import Reports from './views/Reports'
import Reviews from './views/Reviews'
import ReviewsManagement from './views/Reviews/Management'
import ReplyMacros from './views/Reviews/Management/ReplyMacros'
import ReportingMacros from './views/Reviews/Management/ReportingMacros'
import TopicsManagement from './views/Reviews/Management/TopicsManagement'
import ReviewsMonitoring from './views/Reviews/Monitoring'
import Sandbox from './views/Sandbox'
import Summary from './views/Summary'
import Todolists from './views/Todolists'
import TodolistDetails from './views/Todolists/Details'
import TodolistTemplates from './views/TodolistTemplates'
import TodolistTemplateDetails from './views/TodolistTemplates/Details'
import UADashboard from './views/UADashboard'
import UAManagement from './views/UAManagement'
import Unauthorized from './views/Unauthorized'
import Versions from './views/Versions'
import Videos from './views/Videos'
import CategoryDetails from './views/Videos/CategoryDetails'

import { useIsUserAuthorized } from '~/providers/Authorizations'

const { ANALYTICS, APP_SETUP, ASSETS_MANAGER, AUTH, CCC, DYNAMIC_SCREENS, REVIEWS, UA_MANAGEMENT, VERSION } =
  AUTHORIZATIONS

const AuthorizedRoute = ({ authorizations, children }: PropsWithChildren<{ authorizations: string | string[] }>) => {
  const isAuthorized = useIsUserAuthorized(Array.isArray(authorizations) ? [...authorizations] : [authorizations])

  return isAuthorized ? <>{children}</> : <Navigate to="/unauthorized" />
}

const Router = (): ReactElement => {
  /** Local state */

  const { pathname } = useLocation()

  /** Effects */

  useEffect(() => {
    pathname === '/competitors/recommendations' && localStorage.removeItem('recommended-cluster')
  }, [pathname])

  /** Render */

  const deprecatedService = getDeprecatedService(pathname)

  // NOTE: the removed services are no longer reachable, whatever the link the user followed
  if (deprecatedService && isDeprecatedServiceRemoved(deprecatedService)) {
    return <Navigate replace to="/" />
  }

  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Routes>
        <Route element={<Home />} path="/" />
        <Route element={<Summary />} path="summary" />
        <Route element={<AnalyticsRouter />} path="analytics/*" />
        <Route
          element={
            <AuthorizedRoute authorizations={ANALYTICS.UA_DASHBOARD.READ}>
              <UADashboard />
            </AuthorizedRoute>
          }
          path="ua-dashboard"
        />
        <Route element={<Versions />} path="versions" />
        <Route element={<Versions />} path="versions/:version" />
        <Route element={<Checklists />} path="checklists" />
        <Route element={<ChecklistDetails />} path="checklists/:checklistId" />
        <Route
          element={
            <AuthorizedRoute authorizations={UA_MANAGEMENT.READ}>
              <UAManagement />
            </AuthorizedRoute>
          }
          path="ua-management"
        />
        <Route element={<Builds />} path="builds" />
        <Route element={<Builds />} path="builds/:binaryFileId" />
        <Route element={<ExternalLinks />} path="external-links" />
        <Route element={<Events />} path="events" />
        <Route element={<InAppPurchasesRouter />} path="in-app-purchases/*" />
        <Route element={<ASOOverview />} path="aso/overview" />
        <Route element={<ASOAdvanced />} path="aso/advanced" />
        <Route element={<AppConfig />} path="app-config" />
        <Route element={<GraphConfigsList />} path="chart-configs" />
        <Route element={<CompareCohortsRouter />} path="compare-cohorts/*" />
        <Route
          element={
            <AuthorizedRoute authorizations={VERSION.APPLICATION.READ}>
              <AppsManagement />
            </AuthorizedRoute>
          }
          path="apps-management"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={APP_SETUP.TASK.READ}>
              <AppsSetup />
            </AuthorizedRoute>
          }
          path="apps-setup"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={VERSION.APPLICATION.READ}>
              <AppDetail />
            </AuthorizedRoute>
          }
          path="apps-management/:appKey"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={ASSETS_MANAGER.ASSET.READ}>
              <AssetDetail />
            </AuthorizedRoute>
          }
          path="dynamic-screens/assets-management/:assetId"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={ASSETS_MANAGER.ASSET.READ}>
              <AssetsListing />
            </AuthorizedRoute>
          }
          path="dynamic-screens/assets-management"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={DYNAMIC_SCREENS.PLACEMENT.READ}>
              <Placements />
            </AuthorizedRoute>
          }
          path="dynamic-screens/placements"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={DYNAMIC_SCREENS.TEMPLATE.READ}>
              <Templates />
            </AuthorizedRoute>
          }
          path="dynamic-screens/templates"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={DYNAMIC_SCREENS.PAGE_CONTAINER.READ}>
              <PageContainersListing />
            </AuthorizedRoute>
          }
          path="dynamic-screens/page-containers"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={DYNAMIC_SCREENS.PAGE_CONTAINER.READ}>
              <PageContainerDetails />
            </AuthorizedRoute>
          }
          path="dynamic-screens/page-containers/:pageContainerId"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={DYNAMIC_SCREENS.NAVIGATION_GRAPH.READ}>
              <NavigationGraphListing />
            </AuthorizedRoute>
          }
          path="dynamic-screens/navigation-graphs"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={DYNAMIC_SCREENS.NAVIGATION_GRAPH.READ}>
              <NavigationGraphsDetails />
            </AuthorizedRoute>
          }
          path="dynamic-screens/navigation-graphs/:navigationGraphId"
        />
        <Route element={<Arbitration />} path="dynamic-screens/arbitration" />
        <Route path="catalog/procolor/categories">
          <Route
            element={
              <AuthorizedRoute authorizations={CCC.CATALOG.READ}>
                <Categories />
              </AuthorizedRoute>
            }
            index
          />
          <Route
            element={
              <AuthorizedRoute authorizations={CCC.CATALOG.READ}>
                <Categories />
              </AuthorizedRoute>
            }
            path=":categoryId"
          />
        </Route>
        <Route
          element={
            <AuthorizedRoute authorizations={CCC.CATALOG.READ}>
              <Explore />
            </AuthorizedRoute>
          }
          path="catalog/procolor/explore"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={CCC.CATALOG.READ}>
              <CreateTemplate />
            </AuthorizedRoute>
          }
          path="catalog/procolor/create-template"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={CCC.CATALOG.READ}>
              <Daily />
            </AuthorizedRoute>
          }
          path="catalog/procolor/daily"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={REVIEWS.READ}>
              <Reviews />
            </AuthorizedRoute>
          }
          path="reviews"
        >
          <Route element={<ReviewsMonitoring />} index />
          <Route element={<ReviewsMonitoring />} path="monitoring" />
          <Route element={<ReviewsManagement />} path="management">
            <Route element={<ReportingMacros />} index />
            <Route element={<ReportingMacros />} path="reporting-macros" />
            <Route element={<ReplyMacros />} path="reply-macros" />
            <Route element={<TopicsManagement />} path="topics-management" />
          </Route>
        </Route>
        <Route
          element={
            <AuthorizedRoute authorizations={CCC.REPORTS.READ}>
              <Reports />
            </AuthorizedRoute>
          }
          path="reports"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={CCC.REPORTS.READ}>
              <Reports />
            </AuthorizedRoute>
          }
          path="reports/:postId"
        />
        <Route element={<Glossary />} path="glossary" />
        <Route element={<Glossary />} path="glossary/:id" />
        <Route element={<ClustersLayout />} path="competitors/clusters">
          <Route
            element={
              <AuthorizedRoute authorizations={VERSION.CLUSTER.READ}>
                <Clusters />
              </AuthorizedRoute>
            }
            index
          />
          <Route
            element={
              <AuthorizedRoute authorizations={VERSION.CLUSTER.READ}>
                <Feature />
              </AuthorizedRoute>
            }
            path=":clusterId/features-manager"
          />
          <Route
            element={
              <AuthorizedRoute authorizations={VERSION.CLUSTER.READ}>
                <ClustersContextProvider>
                  {/* this is temporary dont look at me */}
                  <ClusterDetails />
                </ClustersContextProvider>
              </AuthorizedRoute>
            }
            path=":clusterId"
          />
          <Route element={<ClusterAppInfos />} path=":clusterId/app-infos/:storeId/device-type/:deviceType" />
        </Route>
        <Route element={<CompetitorsCompanies />} path="competitors/companies" />
        <Route element={<CompetitorsRecommendations />} path="competitors/recommendations" />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTH.AUTHORIZATIONS.READ}>
              <AuthorizationListing />
            </AuthorizedRoute>
          }
          path="mwm-users/authorizations"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.AUTH.AUTHORIZATIONS.READ}>
              <AuthorizationDetails />
            </AuthorizedRoute>
          }
          path="mwm-users/authorizations/:authId"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.AUTH.GROUPS.READ}>
              <GroupListing />
            </AuthorizedRoute>
          }
          path="mwm-users/groups"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.AUTH.GROUPS.READ}>
              <GroupDetails />
            </AuthorizedRoute>
          }
          path="mwm-users/groups/:groupId"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.AUTH.ROLES.READ}>
              <RoleListing />
            </AuthorizedRoute>
          }
          path="mwm-users/roles"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.AUTH.ROLES.READ}>
              <RoleDetails />
            </AuthorizedRoute>
          }
          path="mwm-users/roles/:roleId"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.AUTH.USERS.READ}>
              <Accounts />
            </AuthorizedRoute>
          }
          path="mwm-users/users"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.AUTH.USERS.READ}>
              <AccountDetails />
            </AuthorizedRoute>
          }
          path="mwm-users/users/:userId"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.AUTH.USERS.READ}>
              <AppsUsers />
            </AuthorizedRoute>
          }
          path="accounts/"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.AUTH.USERS.READ}>
              <AppsUsersDetails />
            </AuthorizedRoute>
          }
          path="accounts/:userId/:env"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.AUTH.USERS.READ}>
              <ProfilePosts />
            </AuthorizedRoute>
          }
          path="profiles/:profileId/posts"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.AUTH.USERS.READ}>
              <ProfilePostComments />
            </AuthorizedRoute>
          }
          path="profiles/:profileId/posts/:postId/comments"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.AUTH.USERS.READ}>
              <ProfileProjects />
            </AuthorizedRoute>
          }
          path="profiles/:profileId/projects"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.VERSION.VIDEO.READ}>
              <Videos />
            </AuthorizedRoute>
          }
          path="videos"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.VERSION.VIDEO.READ}>
              <Files />
            </AuthorizedRoute>
          }
          path="my-files"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.VERSION.VIDEO.READ}>
              <Files />
            </AuthorizedRoute>
          }
          path="my-files/:directoryId"
        />
        <Route
          element={
            <AuthorizedRoute authorizations={AUTHORIZATIONS.VERSION.VIDEO.READ}>
              <CategoryDetails />
            </AuthorizedRoute>
          }
          path="videos/:category"
        />
        <Route element={<FileFullScreen />} path="file/:fileId" />
        <Route element={<Releases />} path="releases" />
        <Route element={<TodolistTemplates />} path="todolist-templates" />
        <Route element={<TodolistTemplateDetails />} path="todolist-templates/:templateId" />
        <Route element={<Todolists />} path="todolists" />
        <Route element={<TodolistDetails />} path="todolists/:todolistId" />
        {!isProduction() && <Route element={<Sandbox />} path="sandbox/*" />}
        <Route element={<Unauthorized />} path="unauthorized" />
        <Route element={<NotFound />} path="/404" />
        <Route element={<RedirectRouter service="application" />} path="/application/:appId/*" />
        <Route element={<RedirectRouter service="services" />} path="/services/*" />
        <Route element={<Navigate replace to="/404" />} path="*" />
      </Routes>
    </Suspense>
  )
}

export default Router
